import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import { Button as SetlistButton } from "@opendate-app/setlist";
import { toastError } from "../../../shared/toastHelper";
import darkSelectStyles from "../../../shared/darkSelectStyles";
import MakeRecurringModal, { formatDateForAPI } from "../../ConfirmShow/components/MakeRecurringModal";
import ConfirmPicker from "./ConfirmPicker";
import "../../ConfirmShow/styles/makeRecurringModalStyles.css";
import "../styles/seriesCreateStyles.css";

const NewSeriesModal = ({ open, onClose, teamId, venueOwnerships = [] }) => {
  const [step, setStep] = useState("chooser");
  const [mode, setMode] = useState(null);
  const [picked, setPicked] = useState(null);

  const [name, setName] = useState("");
  const [venueOwnershipId, setVenueOwnershipId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset to step one whenever the modal (re)opens.
  useEffect(() => {
    if (!open) return;
    setStep("chooser");
    setMode(null);
    setPicked(null);
    setSubmitting(false);
  }, [open]);

  const chooseMode = (nextMode) => {
    setMode(nextMode);
    if (nextMode === "collection") {
      // A collection is eventless — created empty, shows added later. No source pick.
      setName("");
      setVenueOwnershipId("");
      setStartDate(null);
      setEndDate(null);
      setStep("collection-form");
    } else {
      setStep("pick");
    }
  };

  const handlePicked = (confirm) => {
    // Only the recurring path picks a source show now; collections are eventless.
    setPicked(confirm);
    setStep("recurring");
  };

  const backToChooser = () => {
    setPicked(null);
    setStep("chooser");
  };

  const redirectToSeries = (id) => {
    window.location = `/teams/${teamId}/event_series/${id}`;
  };

  const submitCollection = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`/teams/${teamId}/event_series`, {
        event_series: {
          name,
          venue_ownership_id: venueOwnershipId,
          start_date: startDate ? formatDateForAPI(startDate) : null,
          end_date: endDate ? formatDateForAPI(endDate) : null,
          grouping: true,
        },
      });
      redirectToSeries(data.id);
    } catch (e) {
      const errs = e && e.response && e.response.data && e.response.data.errors;
      toastError(
        errs
          ? Object.values(errs).flat().join(", ")
          : "Could not create this collection. Please try again."
      );
      setSubmitting(false);
    }
  };

  // Venue is optional — a collection can be venue-less or span venues by design
  // (EventSeries#venue_ownership is optional). Setting one is what unlocks pass
  // sales for the collection, so it's offered but not required.
  const collectionDisabled = submitting || !name.trim() || !startDate || !endDate;

  const venueOptions = venueOwnerships.map((venue) => ({
    value: String(venue.id),
    label: venue.name,
  }));
  const selectedVenue =
    venueOptions.find((option) => option.value === venueOwnershipId) || null;

  // reactjs-popup fires onClose whenever its `open` prop flips to false, so
  // toggling `open` to move between steps would spuriously fire the sibling's
  // onClose and fight the transition. Instead we mount only the active step's
  // popup (unmounting does not fire onClose) and hard-code `open`.
  return (
    <>
      {open && step === "chooser" && (
      <Popup
        open
        className="make-recurring-popup"
        closeOnDocumentClick
        onClose={onClose}
      >
        <div className="popup-modal-container">
          <div className="row m-0 make-recurring-header">
            <div className="col d-flex py-3 px-4">
              <h3 className="mb-0 align-self-center make-recurring-title">
                <strong>New series</strong>
              </h3>
            </div>
            <div className="col-xs-auto d-flex align-items-center pr-4">
              <a
                href="#"
                className="text-dark make-recurring-close"
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                }}
              >
                <i className="fal fa-times" />
              </a>
            </div>
          </div>

          <div className="row m-0">
            <div className="col-12 make-recurring-body">
              <p className="text-muted make-recurring-help">
                How do you want to group these events?
              </p>
              <div className="new-series-chooser">
                <button
                  type="button"
                  className="new-series-option"
                  onClick={() => chooseMode("collection")}
                  data-testid="new-series-collection"
                >
                  <span className="new-series-option-icon">
                    <i className="fal fa-layer-group" />
                  </span>
                  <span>
                    <span className="new-series-option-title">Collection</span>
                    <span className="d-block new-series-option-desc">
                      A curated set of different events — a festival, a takeover.
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="new-series-option"
                  onClick={() => chooseMode("recurring")}
                  data-testid="new-series-recurring"
                >
                  <span className="new-series-option-icon">
                    <i className="fal fa-redo" />
                  </span>
                  <span>
                    <span className="new-series-option-title">Recurring</span>
                    <span className="d-block new-series-option-desc">
                      The same event repeating on a schedule.
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      )}

      {open && step === "pick" && (
        <ConfirmPicker
          open
          onClose={backToChooser}
          teamId={teamId}
          multiSelect={false}
          title="Choose the first event"
          confirmLabel="Continue"
          onConfirm={handlePicked}
        />
      )}

      {open && step === "collection-form" && (
      <Popup
        open
        className="make-recurring-popup"
        closeOnDocumentClick={!submitting}
        onClose={backToChooser}
      >
        <div className="popup-modal-container">
          <div className="row m-0 make-recurring-header">
            <div className="col d-flex py-3 px-4">
              <h3 className="mb-0 align-self-center make-recurring-title">
                <strong>Name your collection</strong>
              </h3>
            </div>
            <div className="col-xs-auto d-flex align-items-center pr-4">
              <a
                href="#"
                className="text-dark make-recurring-close"
                onClick={(e) => {
                  e.preventDefault();
                  if (!submitting) backToChooser();
                }}
              >
                <i className="fal fa-times" />
              </a>
            </div>
          </div>

          <div className="row m-0">
            <div className="col-12 make-recurring-body">
              <p className="text-muted make-recurring-help">
                Group any confirmed events under one collection. You can add more events to the collection once it&rsquo;s created.
              </p>

              <div className="mb-3">
                <label className="form-label make-recurring-label">
                  <strong>Name</strong>
                </label>
                <input
                  type="text"
                  className="confirm-picker-search-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Festival name"
                  data-testid="collection-name"
                />
              </div>

              <div className="mb-3">
                <label className="form-label make-recurring-label">
                  <strong>Venue</strong>{" "}
                  <span className="text-muted" style={{ fontWeight: 400 }}>
                    (optional)
                  </span>
                </label>
                <Select
                  inputId="collection-venue"
                  styles={darkSelectStyles}
                  classNamePrefix="sl-select"
                  options={venueOptions}
                  value={selectedVenue}
                  onChange={(option) => setVenueOwnershipId(option ? option.value : "")}
                  isClearable
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  placeholder="No venue"
                />
                <p className="text-muted mt-1 mb-0" style={{ fontSize: "12px" }}>
                  Set a venue to sell passes on this collection — pass pricing, fees,
                  and taxes are all derived from it.
                </p>
              </div>

              <div className="row mb-4">
                <div className="col-6">
                  <label className="form-label make-recurring-label">
                    <strong>Start date</strong>
                  </label>
                  <div className="flatpickr-date-input px-2 d-flex align-items-center">
                    <div className="pr-2 make-recurring-calendar-icon">
                      <i className="far fa-calendar" />
                    </div>
                    <Flatpickr
                      options={{ dateFormat: "M j, Y" }}
                      placeholder="Select date"
                      value={startDate || ""}
                      onClose={([date]) => setStartDate(date)}
                      data-testid="collection-start"
                    />
                  </div>
                </div>
                <div className="col-6">
                  <label className="form-label make-recurring-label">
                    <strong>End date</strong>
                  </label>
                  <div className="flatpickr-date-input px-2 d-flex align-items-center">
                    <div className="pr-2 make-recurring-calendar-icon">
                      <i className="far fa-calendar" />
                    </div>
                    <Flatpickr
                      options={{ dateFormat: "M j, Y", minDate: startDate || undefined }}
                      placeholder="Select date"
                      value={endDate || ""}
                      onClose={([date]) => setEndDate(date)}
                      data-testid="collection-end"
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end make-recurring-actions">
                <SetlistButton
                  variant="secondary"
                  className="make-recurring-btn"
                  onClick={backToChooser}
                  disabled={submitting}
                >
                  Back
                </SetlistButton>
                <SetlistButton
                  variant="primary"
                  className="make-recurring-btn"
                  onClick={submitCollection}
                  disabled={collectionDisabled}
                  data-testid="collection-submit"
                >
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <strong>Create collection</strong>
                  )}
                </SetlistButton>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      )}

      {open && step === "recurring" && picked && (
        <MakeRecurringModal
          open
          confirm={{
            id: picked.id,
            title: picked.title,
            start_time: picked.start_time,
            event_series: [],
          }}
          team={{ id: teamId }}
          onClose={backToChooser}
          onCreated={(data) => redirectToSeries(data.id)}
        />
      )}
    </>
  );
};

NewSeriesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  venueOwnerships: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ),
};

export default NewSeriesModal;
